import { useCallback, useEffect, useState } from "react";

import { refDe } from "../shared/planilla";
import {
  actualizarEnLibro,
  areaDe,
  bloqueDe,
  insertarTabla,
  leerTabla,
  nombreDeTabla,
  paramsIniciales,
  pesoDe,
  planearActualizacion,
  planearInsercion,
  soltarTabla,
  tablasDe,
  tablasDisponibles,
  TABLAS_HUB,
  textoDeArea,
} from "../shared/tablasHub";

import "./css/tablasHub.css";
import { TOPE_PLANILLA_BYTES, TOPE_PLANILLA_MB } from "../shared/topes";

/* «Insertar tabla del hub» (FiboSheets #4): el botón de la barra del editor y
 * su panel. La lógica —qué tablas hay, cómo se leen, dónde se escriben y qué
 * se pisaría— está en `shared/tablasHub`, pura y probada aparte. Acá sólo se
 * pregunta y se confirma.
 *
 * Sólo lo dibuja el DUEÑO de la planilla (insertar y actualizar escriben, y
 * leen con su sesión y su permiso). Quien la tiene compartida ve los valores
 * ya volcados, que son celdas comunes.
 *
 * El panel es un cartel que se abre encima de la grilla: en la compu, al
 * centro; en el celular, desde abajo y a lo ancho, con botones de dedo (44 px
 * como mínimo) y el selector en una sola columna.
 *
 * Props:
 *   hoja       la hoja a la vista (con su `tablasHub`)
 *   celda      la celda elegida { r, c }, donde va la esquina de la tabla
 *   datos      la capa que lee (`services/hubDatos`)
 *   alCambiar  (fn) => void; fn recibe (hojas, índice de la hoja) y devuelve
 *              el libro nuevo. Pasa por el `mutar` del editor: se deshace.
 *   alElegir   (esquina, otraEsquina) => void, para dejar elegido lo volcado
 *   ahora      () => Date (para los tests)
 */

const miles = (n) => Number(n).toLocaleString("es-AR");
const motivoDe = (e) => (e && (e.motivo || e.message)) || String(e);
const cuandoFue = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("es-AR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "America/Argentina/Buenos_Aires" });
};
const PESO_AVISO = Math.round(TOPE_PLANILLA_BYTES * 0.3); // ~30 % del tope de la planilla entera

/* Lo que se dice después de volcar: cuántas filas, si se recortó, si pesa. */
function textoDeListo(res, area, verbo) {
  const partes = [`${verbo}: ${miles(res.filas.length)} fila${res.filas.length === 1 ? "" : "s"} en ${textoDeArea(area)}.`];
  if (res.filas.length === 0) partes.push("No vino ninguna fila: quedan sólo los encabezados.");
  if (res.recortada) partes.push(`Ojo: eran ${miles(res.total)}; se volcaron las primeras ${miles(res.filas.length)}. Achicá el período o filtrá para traer el resto.`);
  const peso = pesoDe(bloqueDe(res));
  if (peso > PESO_AVISO) partes.push(`Pesa unos ${Math.round(peso / 1000)} KB, y la planilla entera tiene un tope de ${TOPE_PLANILLA_MB} MB para guardarse.`);
  return partes.join(" ");
}

function Campo({ p, valor, onCambio }) {
  const id = `tabla-hub-${p.clave}`;
  let control;
  if (p.tipo === "opcion") {
    control = (
      <select id={id} value={valor ?? ""} onChange={(e) => onCambio(e.target.value)}>
        {p.opciones.map(([k, txt]) => <option key={k || "nada"} value={k}>{txt}</option>)}
      </select>
    );
  } else if (p.tipo === "fecha") {
    control = <input id={id} type="date" value={valor ?? ""} onChange={(e) => onCambio(e.target.value)} />;
  } else if (p.tipo === "numero") {
    control = (
      <input
        id={id} type="number" inputMode="numeric" min={p.min} max={p.max}
        value={valor ?? ""} onChange={(e) => onCambio(e.target.value)}
      />
    );
  } else {
    control = (
      <input
        id={id} type="text" value={valor ?? ""} placeholder={p.ejemplo || ""} autoComplete="off"
        onChange={(e) => onCambio(e.target.value)}
      />
    );
  }
  return (
    <label className="tbh-campo" htmlFor={id}>
      <span>{p.etiqueta}</span>
      {control}
    </label>
  );
}

export default function InsertarTablaHub({ hoja, celda, datos, alCambiar, alElegir, ahora = () => new Date() }) {
  const [abierto, setAbierto] = useState(false);
  const [permisos, setPermisos] = useState(null); // null: cargando
  const [errorPermisos, setErrorPermisos] = useState(null);
  const [fuente, setFuente] = useState(null); // la tabla elegida en el selector
  const [params, setParams] = useState({});
  const [leyendo, setLeyendo] = useState(false);
  const [error, setError] = useState(null);
  const [pendiente, setPendiente] = useState(null); // lo que espera confirmación
  const [listo, setListo] = useState(null); // el mensaje de después

  const tablas = tablasDe(hoja);
  const esquina = celda || { r: 0, c: 0 };

  /* Los permisos se piden al abrir, no al cargar la planilla: quien no usa
     esto no paga un pedido. */
  useEffect(() => {
    if (!abierto) return undefined;
    let vivo = true;
    setPermisos(null);
    setErrorPermisos(null);
    Promise.resolve()
      .then(() => datos.permisos())
      .then((p) => { if (vivo) setPermisos(Array.isArray(p) ? p : []); })
      .catch((e) => { if (vivo) { setPermisos([]); setErrorPermisos(motivoDe(e)); } });
    return () => { vivo = false; };
  }, [abierto, datos]);

  const cerrar = useCallback(() => {
    setAbierto(false);
    setFuente(null);
    setPendiente(null);
    setError(null);
    setLeyendo(false);
  }, []);
  useEffect(() => {
    if (!abierto) return undefined;
    const tecla = (e) => { if (e.key === "Escape") cerrar(); };
    window.addEventListener("keydown", tecla);
    return () => window.removeEventListener("keydown", tecla);
  }, [abierto, cerrar]);

  const abrir = () => { setListo(null); setAbierto(true); };
  const elegirTabla = (f) => { setFuente(f); setParams(paramsIniciales(f, ahora())); setError(null); };

  /* ── Aplicar (ya confirmado, o sin nada que confirmar) ────────────────── */

  const aplicarInsercion = (res, donde) => {
    const momento = ahora();
    alCambiar((hs, i) => hs.map((h, j) => (j === i ? insertarTabla(h, donde, res, { ahora: momento }) : h)));
    const { area } = planearInsercion(hoja, donde, bloqueDe(res));
    if (alElegir) alElegir({ r: area.r0, c: area.c0 }, { r: area.r1, c: area.c1 });
    setListo(textoDeListo(res, area, "Listo"));
    cerrar();
  };
  const aplicarActualizacion = (res, id, correr) => {
    const momento = ahora();
    alCambiar((hs, i) => actualizarEnLibro(hs, i, id, res, { correr, ahora: momento }));
    const plan = planearActualizacion(hoja, id, res);
    setListo(plan ? textoDeListo(res, plan.nueva, "Actualizada") : "Actualizada.");
    cerrar();
  };

  /* ── Insertar ───────────────────────────────────────────────────────────── */

  const insertar = async (e) => {
    e.preventDefault();
    if (!fuente || leyendo) return;
    setLeyendo(true);
    setError(null);
    try {
      const res = await leerTabla(fuente, params, { datos, ahora: ahora() });
      const plan = planearInsercion(hoja, esquina, bloqueDe(res));
      setLeyendo(false);
      if (plan.colisiones.length || plan.tablas.length) {
        setPendiente({ tipo: "insertar", res, plan, donde: esquina });
        return;
      }
      aplicarInsercion(res, esquina);
    } catch (err) {
      setLeyendo(false);
      setError(motivoDe(err));
    }
  };

  /* ── Actualizar ─────────────────────────────────────────────────────────── */

  const actualizar = async (def) => {
    if (leyendo) return;
    setAbierto(true);
    setListo(null);
    setLeyendo(def.id);
    setError(null);
    try {
      const res = await leerTabla(def.fuente, def.params, { datos, ahora: ahora() });
      const plan = planearActualizacion(hoja, def.id, res);
      setLeyendo(false);
      if (!plan) { setError("Esa tabla ya no está en la hoja."); return; }
      if (plan.colisiones.length || plan.otras.length || plan.retocada) {
        setPendiente({ tipo: "actualizar", res, plan, id: def.id });
        return;
      }
      aplicarActualizacion(res, def.id, false);
    } catch (err) {
      setLeyendo(false);
      setError(motivoDe(err));
    }
  };

  const soltar = (def) => {
    alCambiar((hs, i) => hs.map((h, j) => (j === i ? soltarTabla(h, def.id) : h)));
  };
  const irATabla = (def) => {
    const a = areaDe(def);
    if (a && alElegir) alElegir({ r: a.r0, c: a.c0 }, { r: a.r1, c: a.c1 });
    cerrar();
  };

  /* ── Dibujo ─────────────────────────────────────────────────────────────── */

  const disponibles = permisos ? tablasDisponibles(permisos) : [];
  const elegida = fuente ? TABLAS_HUB[fuente] : null;
  const unas = (refs) => refs.slice(0, 4).join(", ") + (refs.length > 4 ? "…" : "");

  let cuerpo;
  if (pendiente) {
    const { plan, res } = pendiente;
    const n = plan.colisiones.length;
    const otras = pendiente.tipo === "insertar" ? plan.tablas : plan.otras;
    cuerpo = (
      <div className="tbh-confirmar" role="alertdialog" aria-label="Confirmar">
        {pendiente.tipo === "insertar" ? (
          <p>
            La tabla ocupa <b>{textoDeArea(plan.area)}</b>
            {` (${miles(res.filas.length)} filas)`} y ahí ya hay cosas escritas.
          </p>
        ) : (
          <p>
            La tabla nueva tiene <b>{miles(res.filas.length)} filas</b> y ocupa <b>{textoDeArea(plan.nueva)}</b>
            {plan.filasDeMas > 0 ? `: ${miles(plan.filasDeMas)} fila${plan.filasDeMas === 1 ? "" : "s"} más que antes.` : "."}
          </p>
        )}
        {n > 0 && <p>Pisaría {n === 1 ? "una celda con algo" : `${miles(n)} celdas con algo`}: <b>{unas(plan.colisiones)}</b>.</p>}
        {otras.length > 0 && (
          <p>Se superpone con {otras.length === 1 ? "otra tabla del hub" : `${otras.length} tablas del hub`} ({otras.map(nombreDeTabla).join("; ")}), que dejaría de poder actualizarse.</p>
        )}
        {pendiente.tipo === "actualizar" && plan.retocada && (
          <p>Insertaste filas o columnas adentro de esta tabla: lo que hayas escrito ahí se va a reemplazar por los datos nuevos.</p>
        )}
        <div className="tbh-botones">
          {pendiente.tipo === "actualizar" && plan.sePuedeCorrer && (
            <button type="button" className="esp-btn tbh-principal" onClick={() => aplicarActualizacion(res, pendiente.id, true)}>
              Correr lo de abajo ({`insertar ${miles(plan.filasDeMas)} fila${plan.filasDeMas === 1 ? "" : "s"}`})
            </button>
          )}
          <button
            type="button" className={"esp-btn" + (pendiente.tipo === "actualizar" && plan.sePuedeCorrer ? "" : " tbh-principal")}
            onClick={() => (pendiente.tipo === "insertar"
              ? aplicarInsercion(res, pendiente.donde)
              : aplicarActualizacion(res, pendiente.id, false))}
          >{n > 0 || otras.length > 0 ? "Pisar igual" : "Seguir"}</button>
          <button type="button" className="esp-btn" onClick={() => setPendiente(null)}>Cancelar</button>
        </div>
      </div>
    );
  } else if (elegida) {
    cuerpo = (
      <form className="tbh-form" onSubmit={insertar}>
        <p className="tbh-ayuda">{elegida.ayuda}</p>
        {elegida.params.map((p) => (
          <Campo key={p.clave} p={p} valor={params[p.clave]} onCambio={(v) => setParams((x) => ({ ...x, [p.clave]: v }))} />
        ))}
        <div className="tbh-botones">
          <button type="submit" className="esp-btn tbh-principal" disabled={!!leyendo}>
            {leyendo ? "Trayendo…" : `Insertar en ${refDe(esquina.r, esquina.c)}`}
          </button>
          <button type="button" className="esp-btn" onClick={() => { setFuente(null); setError(null); }}>← Otra tabla</button>
        </div>
      </form>
    );
  } else {
    cuerpo = (
      <>
        {tablas.length > 0 && (
          <section className="tbh-existentes" aria-label="Tablas de esta hoja">
            <h4>En esta hoja</h4>
            <ul>
              {tablas.map((t) => (
                <li key={t.id}>
                  <span className="tbh-nombre">
                    <b>{nombreDeTabla(t)}</b>
                    <span className="tenue">
                      {` en ${textoDeArea(areaDe(t))}`}
                      {t.actualizada_en ? ` · traída ${cuandoFue(t.actualizada_en)}` : ""}
                      {t.total ? ` · recortada (eran ${miles(t.total)})` : ""}
                    </span>
                  </span>
                  <span className="tbh-acciones">
                    <button type="button" className="esp-btn" disabled={!!leyendo} onClick={() => actualizar(t)}>
                      {leyendo === t.id ? "Trayendo…" : "Actualizar"}
                    </button>
                    <button type="button" className="esp-btn" onClick={() => irATabla(t)}>Ver</button>
                    <button
                      type="button" className="esp-btn" onClick={() => soltar(t)}
                      title="Los valores quedan; deja de poder actualizarse"
                    >Soltar</button>
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}
        <h4>Insertar en {refDe(esquina.r, esquina.c)}</h4>
        {errorPermisos && <p className="tbh-error" role="alert">No se pudieron leer tus permisos: {errorPermisos}</p>}
        {permisos === null ? (
          <p className="tenue">Cargando…</p>
        ) : (
          <div className="tbh-lista">
            {disponibles.map((t) => (
              <button
                key={t.fuente} type="button" className="tbh-opcion" disabled={!t.permitida}
                onClick={() => elegirTabla(t.fuente)}
              >
                <b>{t.titulo}</b>
                <span>{t.permitida ? t.ayuda : "No tenés acceso."}</span>
              </button>
            ))}
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <button
        type="button" className="esp-btn" onClick={abrir}
        title="Volcar una tabla del hub (viajes, flota, vencimientos…) a partir de la celda elegida"
      >▦ Tabla del hub{tablas.length > 0 ? ` (${tablas.length})` : ""}</button>
      {listo && !abierto && (
        <span className="tbh-listo" role="status">
          {listo}
          <button type="button" className="esp-btn chico" onClick={() => setListo(null)} aria-label="Cerrar el mensaje">×</button>
        </span>
      )}
      {abierto && (
        <div className="tbh-fondo" onMouseDown={(e) => { if (e.target === e.currentTarget) cerrar(); }}>
          <div className="tbh-panel" role="dialog" aria-modal="true" aria-label="Insertar tabla del hub">
            <div className="tbh-cabeza">
              <h3>{elegida && !pendiente ? elegida.titulo : "Tabla del hub"}</h3>
              <button type="button" className="esp-btn" onClick={cerrar}>Cerrar</button>
            </div>
            {!pendiente && !elegida && (
              <p className="tbh-sub">
                Se vuelca como valores (encabezados y filas) a partir de la celda elegida, con tus
                permisos. Quien la tenga compartida ve los valores; «Actualizar» es tuyo.
              </p>
            )}
            {error && <p className="tbh-error" role="alert">{error}</p>}
            {cuerpo}
          </div>
        </div>
      )}
    </>
  );
}
